import React, { useState, useEffect, useCallback } from 'react';
import { GenericTable } from 'components/common/GenericTable';
import { getIntroducerColumns } from 'utils/Introducer';
import AddNewIntroducer from 'components/AddNewIntroducer/AddNewIntroducer';
import AddNewAdvisor from 'components/AddNewAdvisor/AddNewAdvisor';
import { Toaster, toast } from 'react-hot-toast';
import { getAllIntroducers } from '_redux/services/introducer.service';
import { editIntroducer } from '_redux/services/introducer.service';
import Upload from 'components/Upload/UploadFile';
import ModalContainer from 'components/common/ModalContainer/ModalContainer';

const Introducer = ({ handleTabChange, tab, tabs }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [selectedRows, setSelectedRows] = useState([]);
  const [data, setData] = useState();
  const [isIntroducerModalOpen, setisIntroducerModalOpen] = useState(false);
  const [isAdvisorModalOpen, setisAdvisorModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [totalData, setTotalData] = useState(0);
  const [updatedRow, setUpdatedRow] = useState({});
  const [checkedAll, setCheckedAll] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [newUpdatedRows, setNewUpdatedRows] = useState({});

  const getIntroducersData = useCallback(async () => {
    try {
      const response = await getAllIntroducers(page, rowsPerPage);
      const total = response.total;
      setTotalData(total);

      setData(prevData => {
        const lastPage = Math.ceil(total / rowsPerPage) - 1;
        if (page === lastPage) {
          const remainingItems = total % rowsPerPage || rowsPerPage;
          return response.data.slice(0, remainingItems);
        }
        return response.data;
      });
    } catch (error) {
      console.error('Error fetching advisors:', error);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    getIntroducersData();
  }, [getIntroducersData]);

  const handleRowSelection = id => {
    setSelectedRows(prev =>
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
    setCheckedAll(false);
  };

  const handleValueChange = (id, field, value) => {
    setData(prevData =>
      prevData.map(row => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const handleAdvisorClick = () => {
    setisAdvisorModalOpen(true);
  };

  const handleIntroducerClick = () => {
    setisIntroducerModalOpen(true);
  };

  const handleAdisorModalClose = () => {
    setisAdvisorModalOpen(false);
  };

  const handleModalClose = () => {
    setisIntroducerModalOpen(false);
  };

  const handleEditToggle = () => {
    setIsEditMode(prev => !prev);
  };
  const handelCancelEdit = () => {
    getIntroducersData();
    setIsEditMode(false);
    setIsDelete(false);
    setSelectedRows([]);
  };

  const handleSaveChanges = async () => {
    try {
      const newRow = await editIntroducer(newUpdatedRows);

      if (newRow.message) {
        toast.success('Row updated successfully!');
        handleEditToggle();
        getIntroducersData();
        setIsEditMode(false);
        setIsDelete(false);
        setSelectedRows([]);
      }
    } catch (error) {
      toast.error(`${error}`);
    }
  };

  const handleUpdateRow = row => {
    setUpdatedRow(row);
  };

  const handleAllChecked = () => {
    const allIds = data?.map(row => row.id) || [];
    const allSelected =
      allIds.length > 0 && allIds.every(id => selectedRows.includes(id));

    if (allSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(allIds);
    }
  };

  const handleArchiveClick = async row => {
    const payload = {
      [row.id]: {
        isActive: false,
      },
    };
    await editIntroducer(payload);
    getIntroducersData();
  };

  const handleUnarchiveClick = async row => {
    const payload = {
      [row.id]: {
        isActive: true,
      },
    };
    await editIntroducer(payload);
    getIntroducersData();
  };

  const columns = getIntroducerColumns({
    data,
    selectedRows,
    setSelectedRows,
    checkedAll,
    setCheckedAll,
    handleAllChecked,
    handleRowSelection,
    handleValueChange,
    isEditMode,
    handleUpdateRow,
    handleArchiveClick,
    handleUnarchiveClick,
    setNewUpdatedRows,
  });

  useEffect(() => {
    const allIds = data?.map(row => row.id) || [];
    const allSelected =
      allIds.length > 0 && allIds.every(id => selectedRows.includes(id));
    setCheckedAll(allSelected);
    if (selectedRows.length > 0) {
      setIsDelete(true);
    } else {
      setIsDelete(false);
    }
  }, [selectedRows, data]);

  const handleImportClick = () => {
    setModalOpen(true);
  };
  const handleFileUploadComplete = useCallback(() => {
    setModalOpen(false);
    getIntroducersData();
  }, []);

  const handleExportSelectedRows = () => {
    if (selectedRows.length === 0) {
      toast.error('No rows selected to export.');
      return;
    }

    const selectedData = data.filter(row => selectedRows.includes(row.id));
    const csvRows = [
      columns.map(col => col.headerName).join(','),
      ...selectedData.map(row =>
        columns.map(col => JSON.stringify(row[col.field] || '')).join(',')
      ),
    ];
    const csvBlob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const csvUrl = URL.createObjectURL(csvBlob);
    const link = document.createElement('a');
    link.href = csvUrl;
    link.download = 'selected_introducers.csv';
    link.click();
    URL.revokeObjectURL(csvUrl);
  };

  return (
    <>
      <Toaster />
      <GenericTable
        title="Set-up"
        refreshData={getIntroducersData}
        addNewIntroducer={true}
        addNewAdvisor={true}
        handleEditToggle={handleEditToggle}
        handleCancelEdit={handelCancelEdit}
        handleSaveChanges={handleSaveChanges}
        isEditMode={isEditMode}
        showEdit={true}
        columns={columns}
        data={data}
        fetchType="introducers"
        setData={setData}
        isIntroducer={true}
        pageConfig={{
          page,
          setPage,
          rowsPerPage,
          setRowsPerPage,
          totalRows: totalData,
        }}
        handleTabChange={handleTabChange}
        onAddIntroducer={handleIntroducerClick}
        onAddAdvisor={handleAdvisorClick}
        tab={tab}
        isTabs={true}
        tabs={tabs}
        isDelete={isDelete}
        ids={selectedRows}
        handleDeleted={handelCancelEdit}
        isImport={true}
        handleImportClick={handleImportClick}
        onExportSelectedRows={handleExportSelectedRows}
        isSelection={selectedRows.length > 0}
      />
      {isIntroducerModalOpen && (
        <AddNewIntroducer
          refreshData={getIntroducersData}
          onClose={handleModalClose}
          open={isIntroducerModalOpen}
          handleClose={handleModalClose}
        />
      )}
      {isAdvisorModalOpen && (
        <AddNewAdvisor
          onClose={handleAdisorModalClose}
          open={isAdvisorModalOpen}
          handleClose={handleAdisorModalClose}
          refreshData={getIntroducersData}
        />
      )}
      <ModalContainer open={isModalOpen}>
        <Upload
          onUploadComplete={handleFileUploadComplete}
          type={'introducers'}
        />
      </ModalContainer>
    </>
  );
};

export default Introducer;
